import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../i18n/LanguageContext';

const LoginModal = ({ isOpen, onClose }) => {
  const { currentLang } = useLanguage();

  if (!isOpen) return null;

  const content = {
    en: {
      title: "Access Your Investment Dashboard",
      subtitle: "Client Portal",
      description: "Monitor performance, documents, and opportunities in one place.",
      features: [
        "Portfolio dashboard",
        "Document center",
        "Deal pipeline",
        "Real-time notifications"
      ],
      login: "Go to Portal Login",
      close: "Close"
    },
    it: {
      title: "Accedi alla Tua Dashboard Investimenti",
      subtitle: "Portale Clienti",
      description: "Monitora performance, documenti e opportunità in un unico posto.",
      features: [
        "Dashboard portfolio",
        "Centro documenti",
        "Pipeline deal",
        "Notifiche in tempo reale"
      ],
      login: "Vai al Login del Portale",
      close: "Chiudi"
    },
    'es-es': {
      title: "Accede a Tu Dashboard de Inversiones",
      subtitle: "Portal de Clientes",
      description: "Monitorea rendimiento, documentos y oportunidades en un solo lugar.",
      features: [
        "Dashboard de portfolio",
        "Centro de documentos",
        "Pipeline de deals",
        "Notificaciones en tiempo real"
      ],
      login: "Ir al Login del Portal",
      close: "Cerrar"
    },
    'es-latam': {
      title: "Accede a Tu Dashboard de Inversiones",
      subtitle: "Portal de Clientes",
      description: "Monitorea rendimiento, documentos y oportunidades en un solo lugar.",
      features: [
        "Dashboard de portfolio",
        "Centro de documentos",
        "Pipeline de deals",
        "Notificaciones en tiempo real"
      ],
      login: "Ir al Login del Portal",
      close: "Cerrar"
    }
  };

  const t_content = content[currentLang] || content.en;

  const handleLogin = () => {
    onClose();
    window.location.href = '/portal/login';
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full max-w-md bg-white rounded-lg shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="login-modal"
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/80 hover:bg-white flex items-center justify-center transition-colors"
            data-testid="close-login-modal"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>

          {/* Header with gradient */}
          <div className="bg-gradient-to-br from-[#1A3A4A] to-[#0f252e] p-8 text-center">
            <div className="w-16 h-16 bg-white/10 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[#60B5AF]" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-white mb-2">
              {t_content.title}
            </h2>
            <span className="inline-block px-4 py-1 bg-[#60B5AF]/20 rounded-full text-[#60B5AF] font-body text-sm font-medium">
              {t_content.subtitle}
            </span>
          </div>

          {/* Content */}
          <div className="p-8">
            <p className="font-body text-[#1A3A4A]/70 text-center mb-6">
              {t_content.description}
            </p>

            {/* Features list */}
            <ul className="space-y-3 mb-8">
              {t_content.features.map((feature, index) => (
                <li key={index} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#60B5AF]/10 flex items-center justify-center flex-shrink-0">
                    <ArrowRight className="w-3 h-3 text-[#60B5AF]" />
                  </div>
                  <span className="font-body text-sm text-[#1A3A4A]/80">{feature}</span>
                </li>
              ))}
            </ul>

            {/* Login CTA */}
            <Button 
              onClick={handleLogin}
              className="w-full bg-[#1A3A4A] text-white hover:bg-[#1A3A4A]/90 rounded-full h-12 font-body font-medium flex items-center justify-center gap-2"
            >
              {t_content.login}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LoginModal;
